import { Component, ElementRef } from '@angular/core';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { ComponentsService } from '../../services/components.service';
import { LayoutEditorBase } from '../layout-editor-base/layout-editor.base';
import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { WorldTimezonesService } from '../services/world-timezones.service';

@Component({
  selector: 'template-component-layout-date',
  templateUrl: './layout-date.component.html',
  styleUrls: ['./layout-date.component.scss'],
  standalone: false,
})
export class LayoutDateComponent extends LayoutEditorBase {
  protected readonly layoutComponent = 'rise-layout-date';

  private DATE_FORMATS = [
    'MMMM D',
    'MMMM DD, YYYY',
    'MMM DD YYYY',
    'MM/DD/YYYY',
    'DD/MM/YYYY',
  ];
  private STACKED_DATE_FORMAT = 'MMMM D';

  public dateFormats = this.DATE_FORMATS.map((format) => {
    return {
      format: format,
      date: moment().format(format),
    };
  });

  LayoutType = ComponentsService.LAYOUTS_MAP['rise-layout-date']; // make layout types available in template
  public WORLD_TIMEZONES = WorldTimezonesService.WORLD_TIMEZONES;

  public dateFormat: string;
  public timezone: string;
  public color: string;
  private componentServices: any;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
  ) {
    super();

    this.componentsService.registerDirective({
      type: 'rise-layout-date',
      element: this.elementRef.nativeElement,
      show: (options?) => {
        if (this.componentsService.selected?.id) {
          this.componentId = this.componentsService.selected.id;
          this.load();
        }
      },
      getLabel: (componentId: string): string => {
        return 'Date';
      },
      getName: (componentId: string): string => {
        return 'Date';
      },
    });
  }

  load() {
    const defaultAttributes =
      this.componentsService.getSelectedDirective().defaultAttributes;
    const defaultLayout = cloneDeep(defaultAttributes?.layout || {});
    const defaultServices = cloneDeep(defaultAttributes?.services || []);

    const componentLayout =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'layout',
      ) || defaultLayout;
    const componentServices =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'services',
      ) || defaultServices;

    // account for situation when "layout" is set as JSON string in html template
    this.componentLayout = this.templateEditorUtils.safeParseJson(
      componentLayout,
      defaultLayout,
    );
    this.componentServices = this.templateEditorUtils.safeParseJson(
      componentServices,
      defaultServices,
    );

    const layoutAttributes = this.componentLayout?.attributes || {};

    this.loadLayoutSelection(this.LayoutType.INLINE);
    this.dateFormat = layoutAttributes.dateFormat || this.DATE_FORMATS[0];
    this.timezone = layoutAttributes.timezone || '';
    this.color = layoutAttributes.color || 'rgba(125,125,125,1)';

    // if environment is not defined, then this is a new component
    // re-save all setting including environment
    const componentEnvironment = this.attributeDataService.getAttributeData(
      this.componentId,
      'environment',
    );
    if (!componentEnvironment) {
      this.save();
    }
  }

  save() {
    this.applyLayoutSelection();
    const applicableDateFormat =
      this.layout === this.LayoutType.STACKED
        ? this.STACKED_DATE_FORMAT
        : this.dateFormat;
    this.componentLayout.attributes = {
      dateFormat: applicableDateFormat,
      timezone: this.timezone,
      color: this.color,
    };

    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      this.componentServices = [{}];
    }
    this.componentServices[0].attributes = {
      type: 'date',
      date: applicableDateFormat,
      timezone: this.timezone,
    };

    this.attributeDataService.setAttributeData(
      this.componentId,
      'layout',
      this.componentLayout,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }

  setLayout(layout: string) {
    this.layout = layout;
    this.save();
  }

  onLayoutKeydown(event: KeyboardEvent, currentLayout: string) {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.navigateToLayout(this.getNextLayout(currentLayout, -1));
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.navigateToLayout(this.getNextLayout(currentLayout, +1));
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        this.setLayout(currentLayout);
        break;
    }
  }

  private getNextLayout(currentLayout: string, index: number): string {
    const layouts = Object.values(this.LayoutType);
    let currentIndex = layouts.indexOf(currentLayout);
    // if current layout is not found, then assume it's the first layout
    currentIndex = currentIndex === -1 ? 0 : currentIndex;
    // circular wrapping using modulo
    const newIndex = (currentIndex + index + layouts.length) % layouts.length;
    return layouts[newIndex];
  }

  private navigateToLayout(layout: string) {
    this.setLayout(layout);
    this.focusLayoutButton();
  }

  private focusLayoutButton() {
    // Use setTimeout to ensure DOM is updated after Angular change detection
    setTimeout(() => {
      const buttons = this.elementRef.nativeElement.querySelectorAll(
        'button.layout-option',
      ) as NodeListOf<HTMLElement>;
      // Find the button that matches the target layout by checking aria-checked
      const targetButton = Array.from(buttons).find((button: HTMLElement) => {
        return button.getAttribute('aria-checked') === 'true';
      }) as HTMLElement;
      if (targetButton) {
        targetButton.focus();
      }
    }, 0);
  }
}
